import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { theme } from 'antd';

export type Theme = 'light' | 'dark';
export type NotificationType = 'info' | 'success' | 'warning' | 'error';

export const getAntdTheme = (currentTheme: Theme) => ({
  algorithm:
    currentTheme === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
  token: {
    colorPrimary: '#1677ff',
    borderRadius: 6,
    ...(currentTheme === 'dark' && {
      colorBgContainer: '#141414',
      colorBgElevated: '#1f1f1f',
      colorText: 'rgba(255, 255, 255, 0.88)',
      colorTextSecondary: 'rgba(255, 255, 255, 0.65)',
    }),
  },
});

export interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  duration?: number;
}

interface Store {
  theme: Theme;
  antdTheme: ReturnType<typeof getAntdTheme>;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;

  notifications: Notification[];
  addNotification: (
    type: NotificationType,
    message: string,
    duration?: number
  ) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;

  isNavigating: boolean;
  setIsNavigating: (value: boolean) => void;
}

export const useStore = create<Store>()(
  persist(
    (set, get) => ({
      theme: 'light',
      antdTheme: getAntdTheme('light'),
      setTheme: (theme) => set({ theme, antdTheme: getAntdTheme(theme) }),
      toggleTheme: () => {
        const newTheme = get().theme === 'light' ? 'dark' : 'light';
        set({ theme: newTheme, antdTheme: getAntdTheme(newTheme) });
      },

      notifications: [],
      addNotification: (type, message, duration = 5000) => {
        const id = Date.now().toString();
        const notification: Notification = { id, type, message, duration };

        set((state) => ({
          notifications: [notification, ...state.notifications],
        }));

        if (duration > 0) {
          setTimeout(() => get().removeNotification(id), duration);
        }
      },
      removeNotification: (id) =>
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        })),
      clearNotifications: () => set({ notifications: [] }),

      isNavigating: false,
      setIsNavigating: (value) => set({ isNavigating: value }),
    }),
    {
      name: 'app-store',
      partialize: (state) => ({ theme: state.theme }),
    }
  )
);
